import { useSession } from "@src/appV2/Auth/api/useSession";
import { useIsHomeHealthWorkplace } from "@src/appV2/ExperimentalHomeHealth/lib/useIsHomeHealthWorkplace";
import { useTu } from "@src/appV2/i18n/useTu";
import { useModalState } from "@src/lib/ui-react";

import { InviteToCaseDialog } from "../components/InviteToCaseDialog";

interface UseInviteToCaseChatActionParams {
  workerId: string;
  workerName: string;
  workplaceId: string;
}

/**
 * Builds the chat invite override for Home Health: the chat's invite button
 * becomes "Invite to case" and opens the per-visit invite dialog, reusing the
 * same affordance the shift invite would.
 */
function useInviteToCaseChatAction(params: UseInviteToCaseChatActionParams) {
  const { workerId, workerName, workplaceId } = params;
  const { tu } = useTu("myProfessionals");
  const modalState = useModalState();

  return {
    label: tu("Invite to case"),
    onClick: modalState.openModal,
    dialog: (
      <InviteToCaseDialog
        modalState={modalState}
        workerId={workerId}
        workerName={workerName}
        workplaceId={workplaceId}
      />
    ),
  };
}

/**
 * Self-gated invite override for chat surfaces (ChatPanel, the full-page
 * chat): returns the invite-to-case action only when the workplace is Home
 * Health and the viewer is a workplace user. Returns `undefined` otherwise so
 * callers fall back to the built-in shift invite.
 */
export function useInviteToCaseChatOverride(params: UseInviteToCaseChatActionParams) {
  const session = useSession();
  const isWorkerSession = session.type === "AGENT";
  const isHomeHealthWorkplace = useIsHomeHealthWorkplace();
  const inviteToCaseAction = useInviteToCaseChatAction(params);

  return isHomeHealthWorkplace && !isWorkerSession ? inviteToCaseAction : undefined;
}
